import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import userApiService from '../../../services/oraganisation-api.service';
import { toast } from 'react-toastify';
import { refreshUserAttributes } from '../../../reducers/authSlice';
import {
  fetchUserAttributes,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { useLoader } from '../../../loader/LoaderContext';
import {
  IconCamera,
  IconPhone,
  IconMail,
  IconLock,
  IconBriefcase,
  IconBuilding,
  IconMapPin,
  IconUserCircle,
  IconEye,
  IconEyeOff,
  IconX,
} from '@tabler/icons-react';
import useUnsavedChanges from '../../../components/UnsavedChanges/useUnsavedChanges';
import UnsavedChangesModal from '../../../components/UnsavedChanges/UnsavedChangesModal';
import FormObserver from '../../../components/UnsavedChanges/FormObserver';
import { Modal, Button } from 'react-bootstrap';
import PasswordRequirements from '../../../components/PasswordRequirements/PasswordRequirements';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import './ProfileDetails.css';

const formatUSPhone = (value) => {
  const phoneNumber = parsePhoneNumberFromString(value, 'US');
  return phoneNumber ? phoneNumber.formatNational() : value;
};

// Get user initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Profile Details Schema
const ProfileSchema = Yup.object().shape({
  name: Yup.string().trim().required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  phonenumber: Yup.string()
    .matches(
      /^\d{10}$/,
      'Enter a valid 10-digit phone number (e.g., 5551234567).',
    )
    .nullable(),
});

// Password Change Schema
const PasswordChangeSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    )
    .required('Password field is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

function ProfileDetails() {
  const { showLoader, hideLoader } = useLoader();
  const userAttributes = useSelector((state) => state.auth.userAttributes);
  const isSuperAdmin = userAttributes['custom:role'] === 'super_admin';
  const organizationAttributes = useSelector(
    (state) => state.auth.organization,
  );
  const dispatch = useDispatch();

  const formikRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const profileImage = userAttributes['profile'] ?? '';
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(profileImage);
  const fileInputRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const passwordFormRef = useRef(null);
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getUserRole = (userType, userTypesMap) => {
    if (isSuperAdmin) {
      return 'Super Admin';
    }
    const roleMap = {
      ORG_OWNER: 'Organization Owner',
    };

    return roleMap[userType] || userTypesMap[userType] || 'User';
  };

  // Extract user information
  const userName = userAttributes['name'] ?? '';
  const userEmail = userAttributes['email'] ?? '';
  const userRole = getUserRole(
    userAttributes['custom:user_type'],
    organizationAttributes?.user_types_map,
  );
  const practiceName = organizationAttributes?.org_name ?? '';
  const userInitials = getInitials(userName);

  const initialValues = {
    name: userAttributes['name'] ?? '',
    email: userAttributes['email'] ?? '',
    phonenumber: userAttributes['custom:user_phone_number'] ?? '',
    user_role: getUserRole(
      userAttributes['custom:user_type'],
      organizationAttributes?.user_types_map,
    ),
    practice_name: organizationAttributes?.org_name ?? '',
    org_street: organizationAttributes?.org_street ?? '',
    org_city: organizationAttributes?.org_city ?? '',
    org_state: organizationAttributes?.org_state ?? '',
    org_zipcode: organizationAttributes?.org_zipcode ?? '',
  };

  // Set has unsaved changes whenever form dirty state or selected image changes
  useEffect(() => {
    setHasUnsavedChanges(isFormDirty || selectedImage !== null);
  }, [isFormDirty, selectedImage]);

  // Handle form dirty state change
  const handleFormDirtyChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  // Reset form function to be passed to the hook
  const resetForm = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }

    // Reset image
    setSelectedImage(null);
    setPreviewImage(profileImage);
    setHasUnsavedChanges(false);
  };

  // Use our custom hook
  const { showUnsavedModal, handleStayOnPage, handleDiscardChanges } =
    useUnsavedChanges({
      isDirty: hasUnsavedChanges,
      resetForm: resetForm,
    });

  const handleSubmit = async (values) => {
    try {
      showLoader();
      await updateUserAttributes({
        userAttributes: {
          name: values.name,
          'custom:user_phone_number': values.phonenumber,
        },
      });
      const userAttributes = await fetchUserAttributes();
      dispatch(refreshUserAttributes(userAttributes));
      toast.success('Profile details updated successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      hideLoader();
    }
  };

  const handlePasswordChange = async (values, { resetForm }) => {
    try {
      showLoader();
      await updatePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      resetForm();
      toast.success('Password updated successfully');
      setShowPasswordModal(false);
      // Notify user by email about the password change
      try {
        userApiService.sendPasswordChangeMail(
          userAttributes?.name,
          userAttributes?.email,
        );
      } catch (error) {
        toast.warning(
          'Password changed successfully, but we could not send confirmation email.',
        );
      }
    } catch (error) {
      if (
        error.__type === 'NotAuthorizedException' ||
        error.name === 'NotAuthorizedException' ||
        error.message.includes('Incorrect username or password')
      ) {
        toast.error('The current password you entered is incorrect');
      } else {
        toast.error(error.message || 'Failed to update password');
      }
    } finally {
      hideLoader();
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Update preview immediately
    setSelectedImage(file);
    const imageUrl = URL.createObjectURL(file);
    setPreviewImage(imageUrl);

    // Upload automatically
    try {
      showLoader();
      const formData = new FormData();
      formData.append('image_type', 'profile_pic');
      formData.append('image_data', file);

      await userApiService.uploadImage(formData);

      // Refresh user attributes to get the updated image URL
      const updatedAttributes = await fetchUserAttributes();
      dispatch(refreshUserAttributes(updatedAttributes));
      setSelectedImage(null);
      toast.success('Profile image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
      // Revert preview on error
      setPreviewImage(profileImage);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      {/* Profile Header Card */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar-large">
              {previewImage ? (
                <img src={previewImage} alt="Profile" />
              ) : (
                <div className="avatar-initials">{userInitials}</div>
              )}
            </div>

            <div className="profile-info">
              <h3 className="profile-name">{userName}</h3>
              {!isSuperAdmin && (
                <>
                  <p className="profile-role">{userRole}</p>
                  <p className="profile-practice">{practiceName}</p>
                </>
              )}
              {isSuperAdmin && <p className="profile-role">{userRole}</p>}
            </div>
          </div>

          <div className="profile-actions">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary btn-upload"
              onClick={handleImageClick}
            >
              <IconCamera size={18} />
              Upload Photo
            </button>
          </div>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={ProfileSchema}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ errors, touched, isSubmitting, dirty }) => (
          <Form className="profile-form">
            <FormObserver onChange={handleFormDirtyChange} />

            {/* Basic Information Section */}
            <div className="form-section">
              <h2 className="section-title">Basic Information</h2>

              <div className="form-stack">
                {/* Full Name */}
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name
                  </label>
                  <div className="input-wrapper">
                    <Field
                      type="text"
                      autoComplete="off"
                      className={`form-control ${
                        errors.name && touched.name ? 'is-invalid' : ''
                      }`}
                      name="name"
                      id="name"
                      placeholder="Enter your full name"
                    />
                    {errors.name && touched.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                </div>

                {/* Phone Number */}
                <div className="form-group">
                  <label htmlFor="phonenumber" className="form-label">
                    Phone Number
                  </label>
                  <div className="input-wrapper input-with-icon">
                    <IconPhone className="input-icon" size={18} />
                    <Field
                      type="text"
                      autoComplete="off"
                      className={`form-control ${
                        errors.phonenumber && touched.phonenumber
                          ? 'is-invalid'
                          : ''
                      }`}
                      name="phonenumber"
                      id="phonenumber"
                      placeholder="(555) 123-4567"
                    />
                    {errors.phonenumber && touched.phonenumber && (
                      <div className="invalid-feedback">
                        {errors.phonenumber}
                      </div>
                    )}
                  </div>
                </div>

                {/* Email Address */}
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <div className="input-wrapper input-with-icon">
                    <IconMail className="input-icon" size={18} />
                    <Field
                      type="email"
                      autoComplete="off"
                      className={`form-control ${
                        errors.email && touched.email ? 'is-invalid' : ''
                      }`}
                      name="email"
                      id="email"
                      disabled={true}
                      readOnly
                    />
                    {errors.email && touched.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>
                </div>

                {/* Password */}
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <div className="password-field-inline">
                    <div className="input-wrapper input-with-icon">
                      <IconLock className="input-icon" size={18} />
                      <input
                        type="password"
                        className="form-control"
                        value="••••••••••••"
                        disabled
                        readOnly
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-change-password-inline"
                      onClick={() => setShowPasswordModal(true)}
                    >
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Role & Practice Information Section */}
            {!isSuperAdmin && (
              <div className="form-section">
                <h2 className="section-title">Role & Practice Information</h2>

                <div className="form-grid form-grid-2">
                  {/* Role */}
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <div className="input-readonly">
                      <IconBriefcase className="input-icon" size={18} />
                      <span>{userRole}</span>
                    </div>
                  </div>

                  {/* Practice Name */}
                  <div className="form-group">
                    <label className="form-label">Practice Name</label>
                    <div className="input-readonly">
                      <IconBuilding className="input-icon" size={18} />
                      <span>{practiceName}</span>
                    </div>
                  </div>
                </div>

                {/* Practice Address Subsection */}
                <div className="subsection">
                  <h3 className="subsection-title">Practice Address</h3>
                  <div className="form-grid form-grid-4">
                    {/* Street */}
                    <div className="form-group">
                      <label className="form-label">Street</label>
                      <Field
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        name="org_street"
                        placeholder="Street"
                        disabled={true}
                        readOnly
                      />
                    </div>

                    {/* City */}
                    <div className="form-group">
                      <label className="form-label">City</label>
                      <Field
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        name="org_city"
                        placeholder="City"
                        disabled={true}
                        readOnly
                      />
                    </div>

                    {/* State */}
                    <div className="form-group">
                      <label className="form-label">State</label>
                      <Field
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        name="org_state"
                        placeholder="State"
                        disabled={true}
                        readOnly
                      />
                    </div>

                    {/* ZIP Code */}
                    <div className="form-group">
                      <label className="form-label">ZIP Code</label>
                      <Field
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        name="org_zipcode"
                        placeholder="ZIP Code"
                        disabled={true}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form Footer */}
            <div className="form-footer">
              <button
                type="submit"
                className="btn btn-primary btn-save"
                disabled={isSubmitting || !dirty}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>

      {/* Password Change Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => {
          setShowPasswordModal(false);
          setShowCurrentPassword(false);
          setShowNewPassword(false);
          setShowConfirmPassword(false);
        }}
        backdrop="static"
        keyboard={false}
        centered
        className="modal password-change-modal"
        size="md"
      >
        <Modal.Header className="password-modal-header">
          <div>
            <Modal.Title>Change Password</Modal.Title>
            <p className="password-modal-subtitle">
              Choose a strong password to keep your account secure
            </p>
          </div>
          <button
            type="button"
            className="btn-close-modal"
            onClick={() => {
              setShowPasswordModal(false);
              setShowCurrentPassword(false);
              setShowNewPassword(false);
              setShowConfirmPassword(false);
            }}
            aria-label="Close"
          >
            <IconX size={20} />
          </button>
        </Modal.Header>
            <Formik
              innerRef={passwordFormRef}
              initialValues={{
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              }}
              validationSchema={PasswordChangeSchema}
              onSubmit={handlePasswordChange}
            >
              {({ errors, touched, isSubmitting, isValid, dirty, values }) => (
                <Form>
                  <Modal.Body className="password-modal-body">
                    {/* Current Password */}
                    <div className="password-field-group">
                      <label htmlFor="currentPassword" className="form-label">
                        Current Password
                      </label>
                      <div className="password-input-wrapper">
                        <IconLock className="password-icon-left" size={18} />
                        <Field
                          type={showCurrentPassword ? 'text' : 'password'}
                          className={`form-control ${
                            errors.currentPassword && touched.currentPassword
                              ? 'is-invalid'
                              : ''
                          }`}
                          name="currentPassword"
                          id="currentPassword"
                          placeholder=""
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          tabIndex={-1}
                        >
                          {showCurrentPassword ? (
                            <IconEyeOff size={18} />
                          ) : (
                            <IconEye size={18} />
                          )}
                        </button>
                      </div>
                      {errors.currentPassword && touched.currentPassword && (
                        <div className="invalid-feedback d-block">
                          {errors.currentPassword}
                        </div>
                      )}
                    </div>

                    {/* New Password */}
                    <div className="password-field-group">
                      <label htmlFor="newPassword" className="form-label">
                        New Password
                      </label>
                      <div className="password-input-wrapper">
                        <IconLock className="password-icon-left" size={18} />
                        <Field
                          type={showNewPassword ? 'text' : 'password'}
                          className={`form-control ${
                            errors.newPassword && touched.newPassword
                              ? 'is-invalid'
                              : ''
                          }`}
                          name="newPassword"
                          id="newPassword"
                          placeholder=""
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          tabIndex={-1}
                        >
                          {showNewPassword ? (
                            <IconEyeOff size={18} />
                          ) : (
                            <IconEye size={18} />
                          )}
                        </button>
                      </div>
                      {values.newPassword && (
                        <PasswordRequirements 
                          password={values.newPassword} 
                          className="mt-3"
                        />
                      )}
                      {errors.newPassword && touched.newPassword && (
                        <div className="invalid-feedback d-block">
                          {errors.newPassword}
                        </div>
                      )}
                    </div>

                    {/* Confirm New Password */}
                    <div className="password-field-group">
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirm New Password
                      </label>
                      <div className="password-input-wrapper">
                        <IconLock className="password-icon-left" size={18} />
                        <Field
                          type={showConfirmPassword ? 'text' : 'password'}
                          className={`form-control ${
                            errors.confirmPassword && touched.confirmPassword
                              ? 'is-invalid'
                              : ''
                          }`}
                          name="confirmPassword"
                          id="confirmPassword"
                          placeholder=""
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <IconEyeOff size={18} />
                          ) : (
                            <IconEye size={18} />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && touched.confirmPassword && (
                        <div className="invalid-feedback d-block">
                          {errors.confirmPassword}
                        </div>
                      )}
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <div className="d-flex align-items-center justify-content-end gap-2 w-100">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setShowPasswordModal(false);
                          setShowCurrentPassword(false);
                          setShowNewPassword(false);
                          setShowConfirmPassword(false);
                        }}
                        className="btn-cancel-password"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        className="btn-submit-password"
                        disabled={isSubmitting || !isValid || !dirty}
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Changing...
                          </>
                        ) : (
                          'Change Password'
                        )}
                      </Button>
                    </div>
                  </Modal.Footer>
                </Form>
              )}
            </Formik>
          </Modal>

      {/* Unsaved Changes Modal component */}
      <UnsavedChangesModal
        show={showUnsavedModal}
        onStay={handleStayOnPage}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
}

export default ProfileDetails;
